import { useState, useEffect, useRef, useCallback } from 'react';
import { Search, Tag } from 'lucide-react';

interface ProductResult {
  name: string;
  price: number;
  promotional_price?: number;
  available: boolean;
  unit: string;
}

export function PriceCheckPage() {
  const [barcode, setBarcode] = useState('');
  const [product, setProduct] = useState<ProductResult | null>(null);
  const [notFound, setNotFound] = useState(false);
  const inputRef = useRef<HTMLInputElement>(null);
  const resetTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const resetScreen = useCallback(() => {
    setBarcode('');
    setProduct(null);
    setNotFound(false);
    inputRef.current?.focus();
  }, []);

  // Auto-reset after 10 seconds when product is displayed
  useEffect(() => {
    if (product || notFound) {
      resetTimerRef.current = setTimeout(resetScreen, 10000);
      return () => {
        if (resetTimerRef.current) clearTimeout(resetTimerRef.current);
      };
    }
  }, [product, notFound, resetScreen]);

  // Auto-focus input on mount
  useEffect(() => {
    inputRef.current?.focus();
  }, []);

  const handleSearch = async (code: string) => {
    if (!code.trim()) return;

    // In production: GET /api/products/barcode/:code
    // Mock lookup for demonstration
    const mockProducts: Record<string, ProductResult> = {
      '7891234567890': { name: 'Arroz Tipo 1 - 5kg', price: 24.9, available: true, unit: 'un' },
      '7891234567891': {
        name: 'Leite Integral 1L',
        price: 5.5,
        promotional_price: 4.4,
        available: true,
        unit: 'un',
      },
      '7891234567892': { name: 'Feijão Carioca 1kg', price: 8.9, available: true, unit: 'un' },
      '7891234567893': { name: 'Carne Bovina Patinho', price: 45.0, available: true, unit: 'kg' },
    };

    const found = mockProducts[code];
    if (found) {
      setProduct(found);
      setNotFound(false);
    } else {
      setProduct(null);
      setNotFound(true);
    }
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    void handleSearch(barcode);
  };

  return (
    <div className="flex min-h-screen flex-col items-center justify-center bg-background p-6">
      <div className="w-full max-w-lg text-center">
        {/* Header */}
        <div className="mb-8">
          <h1 className="text-3xl font-bold text-foreground">Consulta de Preço</h1>
          <p className="mt-2 text-muted-foreground">
            Escaneie ou digite o código de barras do produto
          </p>
        </div>

        {/* Search Input */}
        <form onSubmit={handleSubmit} className="mb-8">
          <div className="relative">
            <Search className="absolute left-4 top-1/2 h-5 w-5 -translate-y-1/2 text-muted-foreground" />
            <input
              ref={inputRef}
              type="text"
              value={barcode}
              onChange={(e) => setBarcode(e.target.value)}
              placeholder="Código de barras..."
              className="w-full rounded-xl border border-border bg-card px-12 py-4 text-lg text-foreground placeholder:text-muted-foreground focus:border-primary focus:outline-none focus:ring-2 focus:ring-primary/20"
              autoComplete="off"
            />
          </div>
        </form>

        {/* Product Result */}
        {product && (
          <div className="animate-in fade-in slide-in-from-bottom-4 rounded-xl border border-border bg-card/50 backdrop-blur-sm p-8">
            <div className="mb-4">
              <h2 className="text-xl font-semibold text-foreground">{product.name}</h2>
              <p className="text-sm text-muted-foreground">
                Unidade: {product.unit === 'kg' ? 'por kg' : 'unidade'}
              </p>
            </div>

            <div className="mb-4">
              {product.promotional_price ? (
                <div>
                  <p className="text-sm text-muted-foreground line-through">
                    R$ {product.price.toFixed(2)}
                  </p>
                  <p className="text-4xl font-bold text-emerald-500">
                    R$ {product.promotional_price.toFixed(2)}
                  </p>
                  <span className="inline-flex items-center gap-1 mt-2 rounded-full bg-emerald-500/10 px-3 py-1 text-sm text-emerald-500">
                    <Tag className="h-3 w-3" />
                    PROMOÇÃO
                  </span>
                </div>
              ) : (
                <p className="text-4xl font-bold text-foreground">
                  R$ {product.price.toFixed(2)}
                </p>
              )}
            </div>

            <div
              className={`inline-flex rounded-full px-3 py-1 text-sm ${
                product.available
                  ? 'bg-emerald-500/10 text-emerald-500'
                  : 'bg-red-500/10 text-red-500'
              }`}
            >
              {product.available ? '✓ Disponível' : '✗ Indisponível'}
            </div>
          </div>
        )}

        {/* Not Found */}
        {notFound && (
          <div className="animate-in fade-in rounded-xl border border-red-500/20 bg-red-500/5 p-8">
            <p className="text-lg font-medium text-red-500">Produto não encontrado</p>
            <p className="mt-1 text-sm text-muted-foreground">
              Verifique o código de barras e tente novamente
            </p>
          </div>
        )}

        {/* Auto-reset indicator */}
        {(product || notFound) && (
          <p className="mt-4 text-xs text-muted-foreground">
            Tela será resetada automaticamente em 10 segundos
          </p>
        )}

        {/* Touch to reset */}
        {(product || notFound) && (
          <button
            onClick={resetScreen}
            className="mt-4 rounded-lg border border-border px-4 py-2 text-sm text-muted-foreground hover:bg-accent/50"
          >
            Nova Consulta
          </button>
        )}
      </div>
    </div>
  );
}
